import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { auth as authApi, http } from "./api";
import { toast } from "sonner";

const AuthCtx = createContext(null);

// Kapan menampilkan peringatan sesi hampir habis (2 menit sebelum expired).
const WARNING_LEAD_MS = 2 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);    // null = still checking, false = not logged in, obj = admin
  const [tech, setTech] = useState(null);    // sama utk teknisi
  const [checked, setChecked] = useState(false);
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);

  function scheduleSessionWarning(tokenExpEpoch) {
    // Clear timers lama
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (!tokenExpEpoch) return;
    const expMs = tokenExpEpoch * 1000;
    const now = Date.now();
    const timeLeft = expMs - now;
    if (timeLeft <= 0) return;
    // Warning 2 menit sebelum expired
    const warnAt = timeLeft - WARNING_LEAD_MS;
    if (warnAt > 0) {
      warnTimerRef.current = setTimeout(() => {
        toast.warning("Sesi login Anda akan berakhir dalam 2 menit. Simpan pekerjaan Anda & login ulang.", {
          duration: 60000,
          id: "session-expiry-warning",
        });
      }, warnAt);
    } else {
      // Kurang dari 2 menit lagi — langsung warn
      toast.warning("Sesi login Anda akan berakhir sebentar lagi. Simpan pekerjaan & login ulang.", {
        duration: 60000,
        id: "session-expiry-warning",
      });
    }
    // Auto-logout tepat saat exp
    logoutTimerRef.current = setTimeout(() => {
      toast.error("Sesi login Anda sudah berakhir. Silakan login ulang.", { id: "session-expired" });
      setUser(false);
    }, timeLeft);
  }

  const refresh = useCallback(async () => {
    // Coba admin dulu, fallback ke tech
    try {
      const u = await authApi.me();
      setUser(u); setTech(false);
      if (u?.token_exp) scheduleSessionWarning(u.token_exp);
    } catch {
      setUser(false);
      try {
        const t = await authApi.techMe();
        setTech(t);
      } catch { setTech(false); }
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Cleanup timer saat unmount
  useEffect(() => () => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
  }, []);

  useEffect(() => {
    const id = http.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401 && checked) {
          if (user) setUser(false);
          if (tech) setTech(false);
        }
        return Promise.reject(err);
      }
    );
    return () => http.interceptors.response.eject(id);
  }, [checked, user, tech]);

  async function login(email, password) {
    const u = await authApi.login(email, password);
    setUser(u); setTech(false);
    if (u?.token_exp) scheduleSessionWarning(u.token_exp);
    return u;
  }

  async function techLogin(username, password) {
    const t = await authApi.techLogin(username, password);
    setTech(t); setUser(false);
    return t;
  }

  async function logout() {
    try {
      if (user) await authApi.logout();
      if (tech) await authApi.techLogout();
    } catch {}
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    setUser(false); setTech(false);
  }

  return (
    <AuthCtx.Provider value={{ user, tech, checked, login, techLogin, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth harus di dalam <AuthProvider>");
  return ctx;
}

export function formatAuthError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Terjadi kesalahan";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(", ");
  return String(d);
}
